/**
 * Bounded waits for anything that crosses the network or a native bridge.
 *
 * Why it exists (9 Oct 2026): on iOS build 2.2.0 (3) "Organize" sat on
 * "Structuring your week…" for ever. The sort awaited the AI relay, the
 * relay awaited a Firebase auth token, and nothing in that chain had a
 * deadline — so a promise that never settled froze the whole screen with
 * no error to show (CLAUDE.md §2: a silent failure is a defect in itself).
 * Every such await now goes through `withTimeout`, and the error it throws
 * NAMES the step that stalled, so the next failure diagnoses itself.
 */

export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${Math.round(ms / 1000)}s`);
    this.name = 'TimeoutError';
  }
}

/** Resolve/reject with `p`, or reject with a TimeoutError naming `label` after `ms`. */
export function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });
  return Promise.race([p, deadline]).finally(() => clearTimeout(timer));
}

/** Deadlines for the Organize path. Total worst case stays well under a minute and a half. */
export const AI_TIMEOUTS = {
  /** Firebase anonymous sign-in + ID token for the relay. Normally < 1s. */
  authMs: 10_000,
  /** One relay/Gemini request. Handwritten scans with thinking take ~10–30s. */
  requestMs: 60_000,
  /** Whole Organize run — the watchdog of last resort in runMagicSort. */
  sortMs: 90_000,
} as const;
